/**
 * Which error the cart is in, and whether re-pricing can clear it.
 *
 * A pricing failure is the only cart error a retry can fix — every other error
 * needs the user to change something (cart contents, store, service mode) or
 * needs ordering to come back up. Keeping that distinction in one place lets the
 * alert and the continue button agree on it.
 */
export type CartErrorKind =
  | 'overCartLimit'
  | 'pricing'
  | 'orderingDisabled'
  | 'storeUnavailable'
  | 'deliveryMinimum'
  | null;

export interface CartErrorStateInput {
  isOverCartLimit: boolean;
  /** The PriceOrder mutation threw. */
  hasPriceMutationError: boolean;
  /** The GetOrder poll resolved to a failure status. */
  priceOrderFailure: boolean;
  isOrderingEnabled: boolean;
  isStoreAvailable: boolean;
  isDeliveryOrder: boolean;
  /** Subtotal is null/undefined — the minimum cannot be judged yet. */
  isCartSubTotalNaN: boolean;
  cartPriceTooLow: boolean;
}

/** The slice of the error state the continue button needs. */
export type CartErrorButtonState = Pick<CartErrorState, 'hasError' | 'canRetryPricing'>;

export interface CartErrorState {
  /** Highest-precedence error, matching the order the alert renders them in. */
  errorKind: CartErrorKind;
  hasError: boolean;
  hasPriceError: boolean;
  /** True when a reprice is the right next action — drives the "Try Again" button. */
  canRetryPricing: boolean;
}

export function getCartErrorState(input: CartErrorStateInput): CartErrorState {
  const {
    isOverCartLimit,
    hasPriceMutationError,
    priceOrderFailure,
    isOrderingEnabled,
    isStoreAvailable,
    isDeliveryOrder,
    isCartSubTotalNaN,
    cartPriceTooLow,
  } = input;

  const hasPriceError = hasPriceMutationError || priceOrderFailure;
  const deliveryMinimumNotMet = isDeliveryOrder && !isCartSubTotalNaN && cartPriceTooLow;

  // Errors a reprice cannot clear. Ordered as the alert renders them.
  const unfixableByRetry =
    isOverCartLimit || !isOrderingEnabled || !isStoreAvailable || deliveryMinimumNotMet;

  let errorKind: CartErrorKind = null;
  if (isOverCartLimit) {
    errorKind = 'overCartLimit';
  } else if (hasPriceError) {
    errorKind = 'pricing';
  } else if (!isOrderingEnabled) {
    errorKind = 'orderingDisabled';
  } else if (!isStoreAvailable) {
    errorKind = 'storeUnavailable';
  } else if (deliveryMinimumNotMet) {
    errorKind = 'deliveryMinimum';
  }

  return {
    errorKind,
    hasError: errorKind !== null,
    hasPriceError,
    canRetryPricing: hasPriceError && !unfixableByRetry,
  };
}
